import { __decorate } from "tslib";
import { Component, Mixins } from 'vue-property-decorator';
import { DocumentType } from '~/db_types';
import { popStoredAuthRedirect } from '~/nasa_ui/utils/authRedirect';
import BaseVue from './nasa_ui/base/BaseVue';
import CurrentUserMixin, { logout } from './nasa_ui/mixins/CurrentUserMixin';
import { UserPermission } from './nasa_ui/types';
import { FRONTEND_CACHE_PREFIX, sortObjectBy } from './nasa_ui/utils';
import { getAssetLogoPath } from './nasa_ui/utils/helpers/assets';
import { ReportingRoutes } from './router';
let LogisticsApp = class LogisticsApp extends Mixins(BaseVue, CurrentUserMixin) {
    DocumentType = DocumentType;
    showModalApplicationChangeLog = false;
    /**
     * Returns the list of report routes that should be exposed/listed.
     *
     * Filters the ReportingRoutes collection to include only entries that have a defined `meta`
     * object and are not flagged with `meta.doNotList`. The resulting array is then sorted by
     * `meta.displayName` using the `sortObjectBy` helper.
     *
     * @returns {CosmicReportRouteConfig[]} Array of route configurations which have `meta` defined
     * and are not marked `doNotList`, sorted by `meta.displayName`.
     *
     * @remarks
     * - The filter operation produces a new array; sorting is applied to that new array and does not
     *   mutate the original `ReportingRoutes` collection.
     * - If `meta.displayName` is missing or equal for multiple routes, their relative order after sort
     *   is determined by the sorting implementation.
     */
    get allRoutesFromRouter() {
        const items = ReportingRoutes.filter((route) => Boolean(route.meta) && !route.meta?.doNotList);
        items.sort(sortObjectBy('meta.displayName'));
        return items;
    }
    get routesForThisContext() {
        return this.allRoutesFromRouter
            .filter((r) => {
            if (!r.meta.supportContexts.length) {
                return true;
            }
            return r.meta.supportContexts.includes(this.currentUserActiveSupportContext);
        })
            .filter((r) => {
            if (!r.meta.permissions.length) {
                return true;
            }
            // Must have every permission listed
            return r.meta.permissions.every((perm) => this.currentUserPermissions[perm]);
        })
            .filter((r) => {
            if (!r.meta.userRoles.length) {
                return true;
            }
            // Must have one of the roles listed
            return r.meta.userRoles.find((role) => this.currentUser.role === role) !== undefined;
        });
    }
    get cosmicLogisticsLogoSrc() {
        return getAssetLogoPath('cosmic_logistics_app_logo_dark.svg');
    }
    get shouldShowCreateMenuDeliveryOrder() {
        if (!this.currentUserActiveSupportContextValidDocumentTypes?.includes(DocumentType.DELIVERY_ORDER)) {
            return false;
        }
        return this.currentUserHasPermission(UserPermission.LOGISTICS_MEGA_ADMIN);
    }
    get shouldShowSideNavAllocationMatrix() {
        if (!this.currentUserActiveSupportContextValidDocumentTypes?.includes(DocumentType.DELIVERY_ORDER)) {
            return false;
        }
        return this.currentUserHasPermission(UserPermission.LOGISTICS_DELIVERY_ORDER_ADMIN);
    }
    get shouldShowSideNavDeliveryOrders() {
        if (!this.currentUserActiveSupportContextValidDocumentTypes?.includes(DocumentType.DELIVERY_ORDER)) {
            return false;
        }
        return this.currentUserHasPermission(UserPermission.LOGISTICS_DELIVERY_ORDER_ADMIN);
    }
    get shouldShowSideNavDeliveryMatrix() {
        if (!this.currentUserActiveSupportContextValidDocumentTypes?.includes(DocumentType.DELIVERY_ORDER)) {
            return false;
        }
        return this.currentUserHasPermission(UserPermission.LOGISTICS_DELIVERY_ORDER_ADMIN);
    }
    get shouldShowSideNavItemListings() {
        return (this.currentUserHasPermission(UserPermission.LOGISTICS_DELIVERY_ORDER_ADMIN) ||
            this.currentUserHasPermission(UserPermission.LOGISTICS_MEGA_ADMIN));
    }
    clearEntireCache() {
        let clearedEntries = 0;
        Object.entries(localStorage).forEach((entry) => {
            const key = entry[0];
            // We do NOT want to clear navOpenState or currentSupportContext
            if (!key || !key.startsWith(FRONTEND_CACHE_PREFIX)) {
                return;
            }
            localStorage.removeItem(key);
            clearedEntries++;
        });
    }
    async created() {
        const authRedirect = popStoredAuthRedirect();
        if (authRedirect) {
            this.$router.replace(authRedirect);
        }
        this.clearEntireCache();
    }
    // called from ui
    logoutUser() {
        logout();
    }
    resetModals() {
        this.showModalApplicationChangeLog = false;
    }
};
LogisticsApp = __decorate([
    Component
], LogisticsApp);
export default LogisticsApp;
